import { useMemo, useState } from "react";
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  DataTable,
  IntentBadge,
  Input,
  SettingsPageShell,
  SettingsSection,
  Switch,
  SetupDriver,
  cn,
  formatRelativeTime,
  type DataTableColumn,
  type SetupCard,
  type SetupInputContext,
} from "@excellent/ui";
import { Plus, X } from "@excellent/ui/icons";
import { useApi, apiCall, apiKey } from "@excellent/ui/api";
import { useSWRConfig } from "swr";
import {
  domains,
  domainModeSchema,
  type CustomDomainRowDto,
  type DomainModeDto,
} from "@excellent/ui/api/contracts/settings/domains";

export interface KindChoice {
  kind: string;
  label: string;
}
export interface ViewChoice {
  uid: string;
  name: string;
  entity_kind: string;
}

const MODE_OPTIONS: Array<{ value: DomainModeDto; label: string }> = [
  { value: "work", label: "Work" },
  { value: "customers", label: "Customers" },
  { value: "product", label: "Product" },
  { value: "operations", label: "Operations" },
  { value: "agents", label: "Agents" },
];

const SLUG_RE = /^[a-z][a-z0-9_-]{1,63}$/;

function slugify(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

/**
 * Custom domains composer (Data Design PRD §6.4 + §6.7). Lists the workspace's
 * custom domains (enabled toggle + mode chip) and drives a SetupDriver walk
 * (mode → name/icon → pick kinds → pick/pin views → home view). Enabled domains
 * appear in their mode's sub-nav immediately (the registry merge in
 * domains.ts + mode-page.tsx). The page owns the create flow; toggling enabled
 * PATCHes the row live.
 */
export function DomainsClient({ kinds = [], views = [] }: { kinds?: KindChoice[]; views?: ViewChoice[] } = {}) {
  const { mutate } = useSWRConfig();
  const [creating, setCreating] = useState(false);

  const { data } = useApi(domains.list, {}, { refreshInterval: 0 });
  const rows = data?.domains ?? [];

  const refresh = () => mutate(apiKey(domains.list, {}));

  const toggleEnabled = async (row: CustomDomainRowDto, enabled: boolean) => {
    await apiCall(domains.update, { body: { slug: row.slug, enabled } });
    await refresh();
  };

  const columns = useMemo<DataTableColumn<CustomDomainRowDto>[]>(
    () => [
      {
        key: "name",
        header: "Name",
        render: (r) => (
          <div className="flex flex-col">
            <span className="font-medium text-foreground">{r.name}</span>
            <span className="font-mono type-subtext text-muted-foreground">{r.slug}</span>
          </div>
        ),
      },
      {
        key: "mode",
        header: "Mode",
        render: (r) => <IntentBadge intent="info">{modeLabel(r.mode)}</IntentBadge>,
      },
      {
        key: "kinds",
        header: "Kinds",
        render: (r) => <span className="text-muted-foreground">{r.member_kinds.length || "—"}</span>,
      },
      {
        key: "views",
        header: "Views",
        render: (r) => <span className="text-muted-foreground">{r.pinned_view_uids.length || "—"}</span>,
      },
      {
        key: "origin",
        header: "Origin",
        render: (r) => (
          <IntentBadge intent={r.origin === "system" ? "neutral" : r.origin === "agent" ? "success" : "info"}>
            {r.origin === "system" ? "System" : r.origin === "agent" ? "Agent" : "User"}
          </IntentBadge>
        ),
      },
      {
        key: "updated",
        header: "Updated",
        render: (r) => <span className="text-muted-foreground">{formatRelativeTime(r.updated_at)}</span>,
      },
      {
        key: "enabled",
        header: "In nav",
        render: (r) => (
          <Switch
            checked={r.enabled}
            onCheckedChange={(v) => void toggleEnabled(r, v)}
            aria-label={`${r.enabled ? "Hide" : "Show"} ${r.name} in the ${modeLabel(r.mode)} nav`}
          />
        ),
      },
    ],
    [],
  );

  return (
    <SettingsPageShell
      title="Domains"
      description="Compose an app as data — object kinds, saved views, and a home under a mode. Enabled domains appear in that mode's sub-nav."
      actions={
        <Button
          size="icon-tb"
          aria-label={creating ? "Cancel" : "New domain"}
          title={creating ? "Cancel" : "New domain"}
          onClick={() => setCreating((c) => !c)}
        >
          {creating ? <X strokeWidth={1.5} className="size-4" /> : <Plus strokeWidth={1.5} className="size-4" />}
        </Button>
      }
    >
      {creating && (
        <SettingsSection title="New domain" description="A guided walk — mode, name, kinds, views, and a home.">
          <DomainComposer
            kinds={kinds}
            views={views}
            existingSlugs={rows.map((r) => r.slug)}
            onCreated={async () => {
              setCreating(false);
              await refresh();
            }}
          />
        </SettingsSection>
      )}

      <SettingsSection title="Your domains" count={rows.length}>
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(r) => r.uid}
          wrapInCard={false}
          empty="No custom domains yet. Create one to add an app to a mode's nav."
        />
      </SettingsSection>
    </SettingsPageShell>
  );
}

function modeLabel(mode: string): string {
  return MODE_OPTIONS.find((m) => m.value === mode)?.label ?? mode;
}

// ── the composer walk ────────────────────────────────────────────────────────

interface Answers {
  mode?: DomainModeDto;
  identity?: { name: string; slug: string; icon: string; slugTouched: boolean };
  kinds?: string[];
  views?: string[];
  home?: string | null;
}

function DomainComposer({
  kinds,
  views,
  existingSlugs,
  onCreated,
}: {
  kinds: KindChoice[];
  views: ViewChoice[];
  existingSlugs: string[];
  onCreated: () => void;
}) {
  const [answers, setAnswers] = useState<Answers>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onAnswer = (card: { key: string }, value: unknown) => {
    setAnswers((a) => ({ ...a, [card.key]: value as never }));
  };

  const submit = async () => {
    const { mode, identity } = answers;
    if (!mode || !identity) {
      setError("Pick a mode and name the domain first.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await apiCall(domains.create, {
        body: {
          slug: identity.slug,
          name: identity.name.trim(),
          icon: identity.icon.trim() || null,
          mode,
          member_kinds: answers.kinds ?? [],
          pinned_view_uids: answers.views ?? [],
          home_view_uid: answers.home ?? null,
        },
      });
      onCreated();
    } catch (e) {
      setError(reasonCopy(e instanceof Error ? e.message : ""));
      setSubmitting(false);
    }
  };

  const onAct = (card: { key: string }) => {
    if (card.key === "review") void submit();
  };

  const cards = buildCards(answers, kinds, views, existingSlugs);

  return (
    <Card className="max-w-2xl">
      <CardContent className="py-6">
        {error ? (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}
        {submitting ? (
          <div className="py-8 type-body text-muted-foreground">Creating your domain…</div>
        ) : (
          <SetupDriver
            cards={cards}
            showDismiss={false}
            onAnswer={onAnswer}
            onAct={onAct}
            onExhausted={onCreated}
            copy={{
              finishTitle: () => "Ready to create",
              finishBody: () => "Press Create on the review card to add it.",
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}

function reasonCopy(message: string): string {
  if (message.startsWith("slug-exists")) return "A domain with that slug already exists.";
  if (message.startsWith("reserved-slug")) return "That slug is reserved — pick a different one.";
  if (message.startsWith("invalid-slug")) return "The slug must be a lowercase slug: [a-z][a-z0-9_-].";
  if (message.startsWith("unknown-mode")) return "That mode isn't one of the six — pick another.";
  if (message.startsWith("unknown-kind")) return "One of the picked kinds no longer exists.";
  if (message.startsWith("unknown-view")) return "One of the picked views no longer exists.";
  return "Couldn't create the domain. Check the composition and try again.";
}

// ── custom prompt inputs ──────────────────────────────────────────────────────

function ModeInput({ ctx }: { ctx: SetupInputContext }) {
  const value = typeof ctx.value === "string" ? ctx.value : "";
  return (
    <div className="flex flex-wrap gap-2">
      {MODE_OPTIONS.map((m) => (
        <button
          key={m.value}
          type="button"
          data-setup-focus={value === m.value ? "" : undefined}
          onClick={() => ctx.setValue(m.value)}
          className={cn(
            "rounded-full border px-3 py-1.5 type-body transition-colors",
            value === m.value
              ? "border-primary/50 bg-primary/10 text-foreground"
              : "border-border/60 text-muted-foreground hover:bg-secondary",
          )}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

interface IdentityValue {
  name: string;
  slug: string;
  icon: string;
  slugTouched: boolean;
}

function IdentityInput({ ctx, existingSlugs }: { ctx: SetupInputContext; existingSlugs: string[] }) {
  const v: IdentityValue =
    ctx.value && typeof ctx.value === "object"
      ? (ctx.value as IdentityValue)
      : { name: "", slug: "", icon: "", slugTouched: false };
  const set = (next: Partial<IdentityValue>) => ctx.setValue({ ...v, ...next });
  const slugValid = SLUG_RE.test(v.slug);
  const taken = existingSlugs.includes(v.slug);
  return (
    <div className="flex flex-col gap-4">
      <label className="flex flex-col gap-1.5">
        <span className="type-body font-medium text-foreground">
          Name <span className="text-destructive">•</span>
        </span>
        <Input
          data-setup-focus
          value={v.name}
          onChange={(e) => {
            const name = e.target.value;
            set({ name, ...(v.slugTouched ? {} : { slug: slugify(name) }) });
          }}
          placeholder="e.g. Logistics"
        />
      </label>
      <label className="flex flex-col gap-1.5">
        <span className="type-body font-medium text-foreground">
          Slug <span className="text-destructive">•</span>
        </span>
        <Input
          value={v.slug}
          onChange={(e) => set({ slug: e.target.value.toLowerCase(), slugTouched: true })}
          placeholder="logistics"
          className="font-mono"
        />
        <span
          className={cn(
            "type-subtext",
            (v.slug && !slugValid) || taken ? "text-destructive" : "text-muted-foreground",
          )}
        >
          {taken
            ? "A domain with that slug already exists."
            : v.slug && !slugValid
              ? "Lowercase letters, numbers, hyphens/underscores; must start with a letter."
              : "The ?area= token in the URL. Can't change later."}
        </span>
      </label>
      <label className="flex flex-col gap-1.5">
        <span className="type-body font-medium text-foreground">Icon</span>
        <Input
          value={v.icon}
          onChange={(e) => set({ icon: e.target.value })}
          placeholder="boxes (optional)"
        />
      </label>
    </div>
  );
}

/** Multi-select chip picker (kinds, then pinned views). */
function ChipPicker({
  options,
  selected,
  onToggle,
  empty,
}: {
  options: Array<{ value: string; label: string; sub?: string }>;
  selected: string[];
  onToggle: (value: string) => void;
  empty: string;
}) {
  if (options.length === 0) return <p className="type-subtext text-muted-foreground">{empty}</p>;
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onToggle(o.value)}
          className={cn(
            "rounded-full border px-3 py-1 type-subtext transition-colors",
            selected.includes(o.value)
              ? "border-primary/50 bg-primary/10 text-foreground"
              : "border-border/60 text-muted-foreground hover:bg-secondary",
          )}
        >
          {o.label}
          {o.sub ? <span className="ml-1 text-muted-foreground">· {o.sub}</span> : null}
        </button>
      ))}
    </div>
  );
}

// ── the card series ────────────────────────────────────────────────────────────

function buildCards(
  answers: Answers,
  kinds: KindChoice[],
  views: ViewChoice[],
  existingSlugs: string[],
): SetupCard[] {
  const selectedKinds = answers.kinds ?? [];
  // Views the domain can pin: those over one of its member kinds (or all views
  // if no kind picked yet, so the walk never dead-ends).
  const eligibleViews = selectedKinds.length
    ? views.filter((v) => selectedKinds.includes(v.entity_kind))
    : views;
  const pinned = answers.views ?? [];
  const homeCandidates = views.filter((v) => pinned.includes(v.uid));

  const reviewLines = answers.identity
    ? [
        `${answers.identity.name} · ${answers.identity.slug}`,
        `Mode: ${modeLabel(answers.mode ?? "")}`,
        `Kinds: ${selectedKinds.join(", ") || "none"}`,
        `Pinned views: ${pinned.length}`,
        `Home: ${views.find((v) => v.uid === answers.home)?.name ?? "the first pinned view"}`,
      ]
    : [];

  return [
    {
      key: "mode",
      mode: "prompt",
      title: "Which mode does this app live in?",
      question: "Which mode does this app live in?",
      sublabel: "A domain lives inside a mode's sub-nav — it never becomes a new mode.",
      initialValue: answers.mode ?? "",
      canSave: (v) => typeof v === "string" && domainModeSchema.safeParse(v).success,
      renderInput: (ctx) => <ModeInput ctx={ctx} />,
    },
    {
      key: "identity",
      mode: "prompt",
      title: "Name your app",
      question: "What is this app called?",
      sublabel: "A name, a URL slug, and an optional icon for the nav tab.",
      initialValue: answers.identity ?? { name: "", slug: "", icon: "", slugTouched: false },
      canSave: (v) => {
        const i = v as IdentityValue | undefined;
        return Boolean(i && i.name.trim() && SLUG_RE.test(i.slug) && !existingSlugs.includes(i.slug));
      },
      renderInput: (ctx) => <IdentityInput ctx={ctx} existingSlugs={existingSlugs} />,
    },
    {
      key: "kinds",
      mode: "prompt",
      title: "Which object kinds does it show?",
      question: "Which object kinds does it show?",
      sublabel: "The kinds this app is about — its quick-links point here. Optional.",
      initialValue: answers.kinds ?? [],
      canSave: () => true, // kinds are optional
      renderInput: (ctx) => {
        const sel = Array.isArray(ctx.value) ? (ctx.value as string[]) : [];
        return (
          <ChipPicker
            options={kinds.map((k) => ({ value: k.kind, label: k.label, sub: k.kind }))}
            selected={sel}
            onToggle={(value) =>
              ctx.setValue(sel.includes(value) ? sel.filter((x) => x !== value) : [...sel, value])
            }
            empty="No object kinds yet — define one in Database → Objects."
          />
        );
      },
    },
    {
      key: "views",
      mode: "prompt",
      title: "Pin its saved views",
      question: "Which saved views should it pin?",
      sublabel: "The views that make up the app's home. Optional — you can pin later.",
      initialValue: answers.views ?? [],
      canSave: () => true,
      renderInput: (ctx) => {
        const sel = Array.isArray(ctx.value) ? (ctx.value as string[]) : [];
        return (
          <ChipPicker
            options={eligibleViews.map((v) => ({ value: v.uid, label: v.name, sub: v.entity_kind }))}
            selected={sel}
            onToggle={(value) =>
              ctx.setValue(sel.includes(value) ? sel.filter((x) => x !== value) : [...sel, value])
            }
            empty={
              selectedKinds.length
                ? "No saved views over those kinds yet — create one in Database → Views."
                : "No saved views yet — create one in Database → Views."
            }
          />
        );
      },
    },
    {
      key: "home",
      mode: "prompt",
      title: "Choose the home view",
      question: "Which view opens first?",
      sublabel: "The landing view when you open the app. Defaults to the first pinned view.",
      initialValue: answers.home ?? null,
      canSave: () => true,
      renderInput: (ctx) => {
        const cur = typeof ctx.value === "string" ? ctx.value : null;
        return (
          <ChipPicker
            options={homeCandidates.map((v) => ({ value: v.uid, label: v.name, sub: v.entity_kind }))}
            selected={cur ? [cur] : []}
            onToggle={(value) => ctx.setValue(cur === value ? null : value)}
            empty="Pin a view in the previous step to choose a home."
          />
        );
      },
    },
    {
      key: "review",
      mode: "action",
      badge: { label: "Review", intent: "info" },
      title: "Create this domain",
      body: answers.identity
        ? `“${answers.identity.name}” will appear in the ${modeLabel(answers.mode ?? "")} nav, rendering its pinned views.`
        : "Fill in the earlier steps first.",
      reasons: reviewLines,
      ctaLabel: "Create domain",
    },
  ];
}
